import { Info } from 'lucide-react';
import { buildEarningTieOut, channelLabel, earningProfitabilityRecaptures,
  type CommissionContextRow, type CommissionEarning } from './myCommissionReportModel';
import type { RecaptureActivity } from '@/services/recaptureService';
import { commissionDate, commissionMoney } from './commissionPresentation';

/** All arithmetic and named totals are supplied by the canonical report model. */
export function EarningDetail({ e, splits, showSplits, maskPricingEvidence, recaptureRecords = null, recipientId }: {
  e: CommissionEarning;
  splits: CommissionContextRow[];
  showSplits: boolean;
  maskPricingEvidence: boolean;
  recaptureRecords?: RecaptureActivity[] | null;
  recipientId?: string;
}) {
  const segments = buildEarningTieOut(e, { maskPricingEvidence, showSplits, recaptureRecords, recipientId });
  const absorption = earningProfitabilityRecaptures(e, recaptureRecords, recipientId);
  return <div className="mc-tieout">
    <div className="mc-tieout-heading"><div><p className="mc-eyebrow">Tie-out</p><h3>{e.loan_number} · {e.borrower_name ?? 'Borrower'}</h3></div>
      <p>{channelLabel(e.payout_type)} · {commissionMoney(e.loan_amount)} · Funded {commissionDate(e.fund_date)}</p></div>
    <div className="mc-waterfall">
      {segments.map(segment => <section key={segment.id} className="mc-segment" aria-label={segment.title}>
        <div className="mc-segment-heading"><h4>{segment.title}</h4><span>{segment.unit === 'points' ? 'Points' : 'Dollars'}</span></div>
        {segment.lines.filter(line => !(segment.id === 'payout' && line.label === 'Cash paid' && line.delta === 0)).map((line, index) => <div key={`${index}-${line.label}`} className={`mc-tie-line mc-line-${line.kind}${line.detail ? ' mc-line-detail' : ''}`}>
          <div className="mc-line-label"><span>{segment.id === 'payout' && line.label === 'Net Payout' ? 'Net earned' : line.label}</span>
            {line.provenance && (segment.id !== 'payout' || index === 0) && <span className={`mc-provenance mc-provenance-${line.provenance}`}>{line.provenance}</span>}
            {line.note && <small>{line.note}</small>}
          </div>
          <div className={`mc-line-delta${line.delta < 0 ? ' mc-negative' : ''}`}>
            {line.kind !== 'total' && (segment.unit === 'points' ? `${line.delta > 0 && line.kind === 'term' && index > 0 ? '+' : ''}${line.delta.toFixed(3)} pts`
              : `${line.delta > 0 && line.kind === 'term' ? '+' : ''}${commissionMoney(line.delta)}`)}
            {line.dollars !== undefined && <small>{commissionMoney(line.dollars)}</small>}
          </div>
          <div className={`mc-line-running${(line.running ?? 0) < 0 ? ' mc-negative' : ''}`}>
            {line.running !== null && (segment.unit === 'points' ? line.running.toFixed(3) : commissionMoney(line.running))}
          </div>
        </div>)}
        {segment.note && <p className="mc-segment-note">{segment.note}</p>}
      </section>)}
    </div>
    {!e.pricing_evidence && !segments.some(segment => segment.id === 'profitability') && <p className="mc-muted">No pricing evidence for this channel.</p>}
    {(absorption.length > 0 || (e.recaptured_amount ?? 0) > 0) && <div className="mc-recapture-callout"><Info aria-hidden="true" />
      <div><strong>Recapture on this record</strong>
        {absorption.length > 0 && <p>Profitability absorption reduces this loan's displayed profit. Period totals count it on its Pacific posting date.</p>}
        {(e.recaptured_amount ?? 0) > 0 && <><p>Commission deducted for recapture: {commissionMoney(e.recaptured_amount ?? 0)}</p><p>See Recapture history for the originating EPO and deduction details.</p></>}
        {recaptureRecords === null && <p>Recapture activity unavailable.</p>}
      </div>
    </div>}
    {showSplits && <section className="mc-splits" aria-label="Splits paid from this loan"><h4>Splits paid from this loan</h4>
      {splits.length ? <><dl>{splits.map((split, index) => <div key={index}><dt>{split.recipient_name || '—'} <span>({channelLabel(split.payout_type)})</span></dt><dd>{commissionMoney(split.net_amount)}</dd></div>)}</dl>
        <p>Company-paid splits from this production — not part of these earnings.</p></> : <p>None.</p>}
    </section>}
  </div>;
}
